/**
 * Tab-scoped signup context carried across the Cognito round trip.
 *
 * Ported from `frontend/src/utils/signup-context.ts`, including the storage
 * key: both apps share an origin and a sessionStorage, so a flow that starts
 * in one and finishes in the other keeps its context.
 *
 * Marketing attribution and `skipPermissionsFlow` used to ride along as Auth0
 * `authorizationParams`. They must not be sent to Cognito - they are PixelMe
 * application state, not authorization-server parameters, and Cognito would
 * reject or ignore arbitrary keys. They are parked here instead and attached
 * to the hub onboarding URL after the callback, when the `/api/v2/users/me`
 * readiness gate answers 409 HUB_REQUIRED for a not-yet-provisioned user.
 *
 * `returnTo` deliberately does NOT live here: it stays in OIDC `state`, so it
 * gets the library's nonce/PKCE/state verification.
 *
 * Read back (once) by whichever app gets the 409 HUB_REQUIRED and builds the
 * hub onboarding URL: `lib/readiness.js` here, or
 * `frontend/src/guards/user-data-guard.tsx` in the v2 app. Both parse it the
 * same way, so keep them in step.
 */
import { MARKETING_PARAMS } from '../utils/marketingParams';

export const SIGNUP_APP_CONTEXT_STORAGE_KEY = 'pixelme:signup_app_context';

const MAX_MARKETING_VALUE_LENGTH = 512;

export const AuthMode = {
  Login: 'login',
  SignUp: 'signup',
};

function isObject(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeMarketingParams(params) {
  if (!isObject(params)) {
    return {};
  }

  return MARKETING_PARAMS.reduce((acc, key) => {
    const rawValue = params[key];

    if (typeof rawValue !== 'string') {
      return acc;
    }

    const normalized = rawValue.trim();

    if (normalized) {
      acc[key] = normalized.slice(0, MAX_MARKETING_VALUE_LENGTH);
    }

    return acc;
  }, {});
}

export function saveSignupAppContext(context) {
  if (typeof window === 'undefined') {
    return;
  }

  const payload = {
    mode: context.mode === AuthMode.SignUp ? AuthMode.SignUp : AuthMode.Login,
    skipPermissionsFlow: !!context.skipPermissionsFlow,
    marketingParams: normalizeMarketingParams(context.marketingParams),
  };

  try {
    window.sessionStorage.setItem(SIGNUP_APP_CONTEXT_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Storage full or blocked: attribution is best-effort, auth must continue.
  }
}

function parseSignupAppContext(raw) {
  try {
    const parsed = JSON.parse(raw);

    if (!isObject(parsed)) {
      return null;
    }

    if (parsed.mode !== AuthMode.Login && parsed.mode !== AuthMode.SignUp) {
      return null;
    }

    if (typeof parsed.skipPermissionsFlow !== 'boolean') {
      return null;
    }

    return {
      mode: parsed.mode,
      skipPermissionsFlow: parsed.skipPermissionsFlow,
      marketingParams: normalizeMarketingParams(parsed.marketingParams),
    };
  } catch {
    return null;
  }
}

/**
 * Reads the signup context exactly once and then clears it, so a retried or
 * refreshed flow can't replay stale attribution.
 */
export function consumeSignupAppContext() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(SIGNUP_APP_CONTEXT_STORAGE_KEY);

    if (!raw) {
      return null;
    }

    window.sessionStorage.removeItem(SIGNUP_APP_CONTEXT_STORAGE_KEY);

    return parseSignupAppContext(raw);
  } catch {
    return null;
  }
}
